import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';

/**
 * SummaryCardのプロパティインターフェース
 * @property {string} title - カードのタイトル
 * @property {target} number - 表示する数値（カウントアップの最終値）
 * @property {string} description - カードの説明文
 * @property {number} [duration=1000] - カウントアップの期間（ms）（任意）
 * @property {string} [className] - カードの追加CSSクラス（任意）
 */
interface SummaryCardProps {
  title: string;
  target: number;
  description?: string;
  duration?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * サマリー情報を表示するカードコンポーネント
 * @param {SummaryCardProps} props - コンポーネントのプロパティ
 * @returns {JSX.Element} サマリーカード要素
 */
const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  target,
  description,
  duration = 1000,
  className = '',
  onClick
}) => {
  const [displayNumber, setDisplayNumber] = useState(0);
  const requestRef = useRef<number | null>(null);
  const previousTargetRef = useRef(0);
  const startTimeRef = useRef<number>(0);

  /**
   * カウントアップアニメーションを実行
   * @param {number} timestamp - アニメーションのタイムスタンプ
   */
  const animateCount = (timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;

    const elapsedTime = timestamp - startTimeRef.current;
    const progress = Math.min(elapsedTime / duration, 1);

    // 前の値から新しい値への変化を計算
    const startValue = previousTargetRef.current;
    const endValue = target;
    const currentValue = Math.floor(startValue + (endValue - startValue) * progress);

    setDisplayNumber(currentValue);

    if (progress < 1) {
      requestRef.current = requestAnimationFrame(animateCount);
    } else {
      // アニメーション完了時に前回値を更新
      previousTargetRef.current = endValue;
      requestRef.current = null;
    }
  };

  useEffect(() => {
    // numberが変更された時のみアニメーションを開始
    if (target !== previousTargetRef.current || requestRef.current === null) {
      startTimeRef.current = 0;

      // 既存のアニメーションをキャンセル
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }

      requestRef.current = requestAnimationFrame(animateCount);
    }

    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }
      requestRef.current = null;
    };
  }, [target, duration]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card className={`summary-card ${className}`}>
      <div className="flex flex-column gap-2">
        <h5 className="text-lg font-medium text-700 mb-1 mt-0">{title}</h5>
        <div
          className={`text-4xl font-bold text-900 ${onClick ? 'cursor-pointer' : ''}`}
          style={{ minHeight: '2.5rem' }}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          aria-label={onClick ? `${title}の詳細を表示` : undefined}
        >
          {displayNumber.toLocaleString()}
        </div>
        {description && (<p className="text-sm text-600 mt-2">{description}</p>)}
      </div>
    </Card>
  );
};

export default SummaryCard;
